import React, { useEffect, useRef, useState } from 'react';
import { productCodeModeIsAny } from 'helpers/constants/cart';
import { sdk } from 'sdk';
import { SuperCartCard } from './SuperCartCard';
import { buildCartAvailabilityRequests, CartAvailabilityRequest, hasP2RDistributionChannel } from './utils';
import { HeaderContentProps } from '../header/types/gmsb-header';

export type SuperCartListProps = {
  data: any;
  contentConfig: HeaderContentProps;
};

export const SuperCartList: React.FC<SuperCartListProps> = ({ data, contentConfig }) => {
  const extensions = sdk.composableCommerce;
  const [pickupStores, setPickupStores] = useState<Record<string, any>>({});
  const fetchedCartIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!data?.length) return;
    if (!hasP2RDistributionChannel(data)) return;

    const requests = buildCartAvailabilityRequests(data).filter((request) => {
      // Tracked per cart so a cart whose store info arrives late still gets a
      // request on a later data update instead of being latched out forever.
      if (fetchedCartIdsRef.current.has(request.cartId)) return false;

      if (!request.storeInfo?.fulfillerId || !request.storeInfo?.storeNumber) {
        console.error('availabilityTimeStamp error: storeInfos are incomplete');
        return false;
      }

      if (!request.lineItems.length) {
        console.error('availabilityTimeStamp error: timestampLineItems is empty');
        return false;
      }

      return true;
    });

    if (!requests.length) return;

    const run = async (request: CartAvailabilityRequest) => {
      try {
        const response = await extensions.store.availabilityTimeStamp({
          payload: {
            storePickupInfos: [request.storeInfo],
            lineItems: request.lineItems,
            productCodeMode: productCodeModeIsAny,
          },
          parallel: true,
        });

        if (response.isError || !response.data?.storePickupInfos?.length) return;

        const storePickupInfo =
          response.data.storePickupInfos.find((info: any) => info?.storeNumber === request.storeInfo.storeNumber) ??
          response.data.storePickupInfos[0];

        setPickupStores((previous) => ({ ...previous, [request.cartId]: storePickupInfo }));
      } catch (err) {
        console.error('availabilityTimeStamp error', err);
      }
    };

    requests.forEach((request) => fetchedCartIdsRef.current.add(request.cartId));
    void Promise.all(requests.map(run));
  }, [data]);

  return (
    <div className="space-y-4 px-24 lg:p-36 lg:pt-0" data-testid="super-cart-list">
      {data?.map((cart: any, idx: any) => (
        <SuperCartCard
          pickupStore={pickupStores[cart?.cartId]}
          key={`${cart.cartId}-${idx}`}
          cart={cart}
          contentConfig={contentConfig}
          idx={idx}
        />
      ))}
    </div>
  );
};
